import React, { useContext, useState, useEffect, useRef } from "react";

import IconButton from "@material-ui/core/IconButton";
import Popover from "@material-ui/core/Popover";
import Switch from "@material-ui/core/Switch";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";

import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import NotificationsOffIcon from "@material-ui/icons/NotificationsOff";
import NotificationsPausedIcon from "@material-ui/icons/NotificationsPaused";

import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import { i18n } from "../../translate/i18n";
import { fmtTime } from "../../translate/localeHelper";
import {
  nextBusinessOpening,
  parseBusinessHours,
  canComputeNextOpening
} from "../../helpers/businessHours";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const DEFAULT_PREFS = {
  sound: true,
  browser: true,
  mutedUntil: null,
  snoozeReason: null
};

const useStyles = makeStyles(theme => ({
  iconButton: {
    color: theme.palette.text.primary
  },
  paper: {
    padding: theme.spacing(2),
    minWidth: 290,
    maxWidth: 320
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  snoozeRow: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
    marginLeft: -4,
    marginRight: -4
  },
  snoozeBtn: {
    flex: "1 1 auto",
    margin: 4,
    minWidth: 52
  },
  wideBtn: {
    marginTop: theme.spacing(1),
    width: "100%"
  }
}));

/**
 * feat_316 — preferencias de notificación del propio usuario.
 *
 * Persiste en `Users.notificationPreferences` reusando `PUT /users/:id`, que ya
 * emite el socket `user {action:"update"}` → todas las pestañas se sincronizan
 * solas (useAuth ya escucha ese evento y gatea por id).
 */
const NotificationSettingsButton = () => {
  const classes = useStyles();
  const { user, realUser } = useContext(AuthContext);
  const { getSetting } = useContext(SettingsContext);

  const [anchorEl, setAnchorEl] = useState(null);
  // Fuerza el re-render del ícono cuando vence el snooze (el estado real llega
  // por socket tras el PUT, pero el ícono debe reflejar el vencimiento ya).
  const [, setTick] = useState(0);
  const savingRef = useRef(false);

  // ⚠ feat_299 "Ver como usuario": AuthContext.user es el TARGET; las
  // preferencias son del admin REAL que está logueado, no del target.
  const selfUser = realUser || user;
  const editingUserId = selfUser?.id;
  const prefs = { ...DEFAULT_PREFS, ...(selfUser?.notificationPreferences || {}) };

  const isPermanent = prefs.mutedUntil === "permanent";
  const mutedUntilIso =
    prefs.mutedUntil && prefs.mutedUntil !== "permanent" ? prefs.mutedUntil : null;
  const mutedUntilDate = mutedUntilIso ? new Date(mutedUntilIso) : null;
  const validUntil =
    mutedUntilDate && !isNaN(mutedUntilDate.getTime()) ? mutedUntilDate : null;

  const now = new Date();
  const isSnoozed = !!validUntil && validUntil > now;
  const isActive = !isPermanent && !isSnoozed;

  const savePreferences = async patch => {
    if (!editingUserId) return;
    try {
      savingRef.current = true;
      await api.put(`/users/${editingUserId}`, {
        notificationPreferences: { ...prefs, ...patch }
      });
      // El socket user:update actualiza el AuthContext en TODAS las pestañas.
    } catch (err) {
      toastError(err);
    } finally {
      savingRef.current = false;
    }
  };

  // Auto-reactivación al vencer el snooze temporal.
  // El tick corre en toda pestaña (para que el ícono no quede desactualizado)
  // pero el PUT lo dispara SOLO la pestaña visible — guard anti-tormenta: si N
  // pestañas ocultas escribieran, serían N escrituras simultáneas del mismo valor.
  // Dep = el ISO (string estable), NUNCA el objeto Date: un Date nuevo por render
  // reiniciaría el intervalo en cada render y no llegaría a disparar nunca.
  useEffect(() => {
    if (!mutedUntilIso) return undefined;

    const check = () => {
      const until = new Date(mutedUntilIso);
      if (isNaN(until.getTime())) return;
      if (until > new Date()) return;
      setTick(t => t + 1);
      if (document.visibilityState !== "visible") return;
      if (savingRef.current) return;
      savePreferences({ mutedUntil: null, snoozeReason: null });
    };

    check(); // por si ya venció mientras la pestaña estaba cerrada/oculta
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutedUntilIso, editingUserId]);

  const closePopover = () => setAnchorEl(null);

  const snoozeFor = hours => {
    const until = new Date(Date.now() + hours * 3600 * 1000);
    savePreferences({ mutedUntil: until.toISOString(), snoozeReason: `${hours}h` });
    closePopover();
  };

  const rawBusinessHours = getSetting("businessHours");
  const always247 = getSetting("always247") === "true";
  const canSnoozeUntilOpening = canComputeNextOpening(rawBusinessHours, always247);

  const snoozeUntilOpening = () => {
    const opening = nextBusinessOpening(
      new Date(),
      parseBusinessHours(rawBusinessHours),
      getSetting("businessTimezone"),
      always247
    );
    // No debería pasar (el botón está oculto en ese caso), pero nunca inventamos
    // una apertura que no existe.
    if (!opening) return;
    savePreferences({ mutedUntil: opening.toISOString(), snoozeReason: "next-day" });
    closePopover();
  };

  const mutePermanent = () => {
    savePreferences({ mutedUntil: "permanent", snoozeReason: "permanent" });
    closePopover();
  };

  const reactivate = () => {
    savePreferences({ mutedUntil: null, snoozeReason: null });
    closePopover();
  };

  const toggleSound = () => savePreferences({ sound: !prefs.sound });

  const browserSupported = typeof window !== "undefined" && "Notification" in window;
  const browserDenied = browserSupported && Notification.permission === "denied";
  const canUseBrowserNotif = browserSupported && !browserDenied;

  const toggleBrowser = async () => {
    // requestPermission() SOLO al ACTIVAR y desde el click (gesto de usuario real).
    // Con permission "denied" no hay vuelta desde JS: el usuario tiene que ir al
    // candado de la barra de direcciones — por eso el toggle queda deshabilitado.
    if (!prefs.browser && browserSupported && Notification.permission === "default") {
      let result = "denied";
      try {
        result = await Notification.requestPermission();
      } catch {
        result = "denied";
      }
      if (result !== "granted") {
        toast.warn(i18n.t("notificationPrefs.browserDenied"));
        return;
      }
    }
    savePreferences({ browser: !prefs.browser });
  };

  let Icon = NotificationsActiveIcon;
  let tooltip = i18n.t("notificationPrefs.active");
  if (isPermanent) {
    Icon = NotificationsOffIcon;
    tooltip = i18n.t("notificationPrefs.mutedPermanent");
  } else if (isSnoozed) {
    Icon = NotificationsPausedIcon;
    tooltip = i18n.t("notificationPrefs.snoozedUntil", {
      time: fmtTime(validUntil, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      })
    });
  }

  if (!editingUserId) return null;

  return (
    <>
      <Tooltip title={tooltip} arrow>
        <IconButton
          onClick={e => setAnchorEl(e.currentTarget)}
          aria-label={i18n.t("notificationPrefs.title")}
          className={classes.iconButton}
        >
          <Icon />
        </IconButton>
      </Tooltip>

      <Popover
        disableScrollLock
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        classes={{ paper: classes.paper }}
      >
        <Typography variant="subtitle1">
          {i18n.t("notificationPrefs.title")}
        </Typography>

        <div className={classes.row} style={{ marginTop: 8 }}>
          <span>{i18n.t("notificationPrefs.active")}</span>
          <Switch
            checked={isActive}
            color="primary"
            onChange={isActive ? mutePermanent : reactivate}
          />
        </div>

        <Divider style={{ margin: "12px 0" }} />

        <Typography variant="body2" color="textSecondary">
          {i18n.t("notificationPrefs.snoozeFor")}
        </Typography>
        <div className={classes.snoozeRow}>
          {[1, 4, 8, 24].map(h => (
            <Button
              key={h}
              size="small"
              variant="outlined"
              className={classes.snoozeBtn}
              onClick={() => snoozeFor(h)}
            >
              {`${h}h`}
            </Button>
          ))}
        </div>

        {/* Fail-safe: sin businessHours o con always247, la opción NO se ofrece */}
        {canSnoozeUntilOpening && (
          <Button
            size="small"
            variant="outlined"
            className={classes.wideBtn}
            onClick={snoozeUntilOpening}
          >
            {i18n.t("notificationPrefs.untilOpening")}
          </Button>
        )}

        <Button
          size="small"
          variant="outlined"
          className={classes.wideBtn}
          onClick={mutePermanent}
        >
          {i18n.t("notificationPrefs.mutePermanent")}
        </Button>

        <Divider style={{ margin: "12px 0" }} />

        <div className={classes.row}>
          <span>{i18n.t("notificationPrefs.sound")}</span>
          <Switch checked={!!prefs.sound} color="primary" onChange={toggleSound} />
        </div>

        <div className={classes.row} style={{ marginTop: 4 }}>
          <span>{i18n.t("notificationPrefs.browser")}</span>
          <Switch
            checked={!!prefs.browser && canUseBrowserNotif}
            color="primary"
            onChange={toggleBrowser}
            disabled={!canUseBrowserNotif}
          />
        </div>
        {!canUseBrowserNotif && (
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: "block", marginTop: 4 }}
          >
            {`⚠ ${i18n.t("notificationPrefs.browserBlocked")}`}
          </Typography>
        )}

        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginTop: 12 }}
        >
          {i18n.t("notificationPrefs.badgeNote")}
        </Typography>
      </Popover>
    </>
  );
};

export default NotificationSettingsButton;
